import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "orphion_token";

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t) => localStorage.setItem(TOKEN_KEY, t);
export const clearToken = () => localStorage.removeItem(TOKEN_KEY);

export const api = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
  withCredentials: true, // send the httpOnly session cookie (Emergent Google sign-in)
});

api.interceptors.request.use((config) => {
  const t = getToken();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

export const submitContact = (payload) => api.post("/contact", payload).then((r) => r.data);
export const listContacts = () => api.get("/contact").then((r) => r.data);

export const login = (email, password) =>
  api.post("/auth/login", { email, password }).then((r) => r.data);
export const fetchMe = () => api.get("/auth/me").then((r) => r.data);
export const changePassword = (current_password, new_password) =>
  api.post("/auth/change-password", { current_password, new_password }).then((r) => r.data);

// Emergent-managed Google sign-in — exchange session_id for our JWT (also sets httpOnly cookie server-side)
export const googleExchangeSession = (sessionId) =>
  api
    .post("/auth/google/session", { session_id: sessionId }, { withCredentials: true })
    .then((r) => r.data);

// Clear session cookie server-side + best-effort session row cleanup
export const apiLogout = () =>
  api.post("/auth/logout", {}, { withCredentials: true }).then((r) => r.data);

// Jobs
export const listJobs = (params) =>
  api.get("/jobs", { params, paramsSerializer: { indexes: null } }).then((r) => r.data);
export const jobFacets = () => api.get("/jobs/facets").then((r) => r.data);
export const getJob = (id) => api.get(`/jobs/${id}`).then((r) => r.data);

// Candidates
export const parseCv = (file) => {
  const form = new FormData();
  form.append("file", file);
  return api
    .post("/candidates/parse-cv", form, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 60000,
    })
    .then((r) => r.data);
};
export const submitCandidate = (payload) =>
  api.post("/candidates", payload).then((r) => r.data);
export const listCandidates = () => api.get("/candidates").then((r) => r.data);

// Saved searches
export const saveSearch = (payload) =>
  api.post("/saved-searches", payload).then((r) => r.data);
export const listSavedSearches = (email) =>
  api.get("/saved-searches", { params: { email } }).then((r) => r.data);
export const deleteSavedSearch = (id, email) =>
  api.delete(`/saved-searches/${id}`, { params: { email } }).then((r) => r.data);

// Blog / Insights
export const listBlog = () => api.get("/blog").then((r) => r.data);
export const getBlogPost = (slug) => api.get(`/blog/${slug}`).then((r) => r.data);
export const subscribeNewsletter = (payload) =>
  api.post("/newsletter/subscribe", payload).then((r) => r.data);

// Blog admin CRUD
export const adminListBlog = () => api.get("/admin/blog").then((r) => r.data);
export const adminGetBlog = (id) => api.get(`/admin/blog/${id}`).then((r) => r.data);
export const adminCreateBlog = (payload) => api.post("/blog", payload).then((r) => r.data);
export const adminUpdateBlog = (id, payload) => api.patch(`/admin/blog/${id}`, payload).then((r) => r.data);
export const adminDeleteBlog = (id) => api.delete(`/admin/blog/${id}`).then((r) => r.data);

// Uploads (admin)
export const uploadBlogCover = async (file) => {
  const fd = new FormData();
  fd.append("file", file);
  const r = await api.post("/admin/upload/blog-cover", fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return r.data; // { path, url, size, content_type }
};

// Absolute URL builder for media served from the backend
export const mediaUrl = (relOrAbs) => {
  if (!relOrAbs) return "";
  if (/^https?:\/\//i.test(relOrAbs)) return relOrAbs;
  return `${API}${relOrAbs}`;
};

// CV download (protected — admin or matching client)
export const candidateCvUrl = (candidateId) => `${API}/candidates/${candidateId}/cv`;

// Clients (admin)
export const listClients = () => api.get("/clients").then((r) => r.data);
export const createClient = (payload) => api.post("/clients", payload).then((r) => r.data);
export const updateClient = (id, payload) => api.patch(`/clients/${id}`, payload).then((r) => r.data);
export const deleteClient = (id) => api.delete(`/clients/${id}`).then((r) => r.data);

// Client dashboard (self)
export const getClientDashboard = () => api.get("/client/dashboard").then((r) => r.data);

// Dynamic OG image URL
export const ogUrl = (title, kicker = "orphionglobalhr.com") => {
  const q = new URLSearchParams({ title, kicker }).toString();
  return `${API}/og?${q}`;
};
